<template>
    <NewHomeCard :card-type="CARD_TYPE.ACTION_ITEMS">
        <template #title>
            <div class="title-body flex justify-between items-center" :class="[{ 'text-white': theme === 'primary' }]">
                {{ t("chat.home.actionItems.title") }}
                <span class="h-10 min-w-10">
                    <ProgressCircle :progress="progress.percent" :content="progress.content" :aria-label="t('chat.home.actionItems.progressLabel')" />
                </span>
            </div>
        </template>
        <template #content>
            <div class="space-y-2">
                <BaseCard
                    v-for="{ item, id } in actionItems"
                    :key="id"
                    :disabled="item.checked"
                    as="button"
                    padding="sm"
                    type="button"
                    class="body text-left flex gap-2 fs-mask w-full"
                    :class="{ 'bg-chip-status-done-bg text-chip-status-done-text': item.checked }"
                    :title="lineClamp > 0 ? item.content : undefined"
                    @click="openActionItemChat(id, item)">
                    <span aria-hidden="true" class="flex h-5 min-w-5 max-w-5 items-center justify-center overflow-hidden rounded border border-control-border-strong bg-control-bg">
                        <i v-if="item.checked" class="bi bi-check-square text-xl/none text-control-accent" aria-hidden="true"></i>
                    </span>
                    <span v-if="item.checked" class="sr-only">{{ t("chat.home.actionItems.completedLabel") }}</span>
                    <p :class="`body-small line-clamp-${lineClamp}`">{{ item.content }}</p>
                </BaseCard>
            </div>
        </template>
    </NewHomeCard>
</template>

<script setup>
import { useFetch } from "~vue/api";
import BaseCard from "~vue/components/BaseCard.vue";
import ProgressCircle from "~vue/components/ProgressCircle.vue";
import { openUrl } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { CHATSOURCE, HOMEPAGE_CARD_LOCATION } from "~vue/utils/constants";
import { CARD_TYPE } from "~vue/utils/homeCards";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import NewHomeCard from "./NewHomeCard.vue";

const { actionItems, default_chat_redirect_url } = defineProps({
    actionItems: { type: Array, required: true },
    theme: { type: String, default: "secondary" },
    default_chat_redirect_url: { type: String, required: true },
    lineClamp: { type: Number, default: 0 },
});

const { t } = useI18n();

onMounted(() => logUserInteraction(AmplitudeEventNames.ACTION_ITEMS_SHOWN, { contents: actionItems.map(({ item }) => item.content) }));

const progress = computed(() => {
    const actionItemCount = actionItems.length;
    const completed = actionItems.filter(({ item }) => item.checked).length;

    return {
        percent: (completed / actionItemCount) * 100,
        content: `${completed}/${actionItemCount}`,
    };
});

const payload = ref(null);
const { onFetchError, execute } = useFetch("/accounts/action_items", { immediate: false }).post(payload).json();
onFetchError(() => {
    logError("Failed to update action item");
});

const openActionItemChat = async (id, item) => {
    logUserInteraction(AmplitudeEventNames.HOMEPAGE_CARD_CLICKED, { card_type: CARD_TYPE.ACTION_ITEMS, content: item.content, location: HOMEPAGE_CARD_LOCATION.HOME });
    if (!item.checked) {
        payload.value = JSON.stringify({ id, action_item: { ...item, checked: true } });
        await execute();
    }
    const base_url = item.chat_url ?? default_chat_redirect_url;
    openUrl(base_url, { queryParams: { chat_source: CHATSOURCE.HOME_ACTION_ITEMS } });
};
</script>
